
import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import XIcon from './icons/XIcon';
import WeekChecklist from './WeekChecklist';
import type { Language, MealPlan, WeekStartDay } from '../types';
import { getTranslation, isRTL } from '../utils/translations';

interface CompactWeekSelectorProps {
    isOpen: boolean;
    onClose: () => void;
    onSync: (selectedIndices: number[]) => void;
    weeks: Date[][];
    mealPlans: Record<string, MealPlan>;
    language?: Language;
    weekStartDay?: WeekStartDay;
}

const CompactWeekSelector: React.FC<CompactWeekSelectorProps> = ({
    isOpen,
    onClose,
    onSync,
    weeks,
    mealPlans,
    language,
    weekStartDay
}) => {
    const t = (key: any) => getTranslation(language, key);
    const rtl = isRTL(language);
    const [selectedWeeks, setSelectedWeeks] = useState<Set<number>>(new Set());

    // Pre-select ALL weeks when opening
    useEffect(() => {
        if (isOpen && weeks.length > 0) {
            const allIndices = weeks.map((_, i) => i);
            setSelectedWeeks(new Set(allIndices));
        }
    }, [isOpen, weeks]);

    if (!isOpen) return null;

    const toggleWeek = (idx: number) => {
        setSelectedWeeks(prev => {
            const next = new Set(prev);
            if (next.has(idx)) next.delete(idx); else next.add(idx);
            return next;
        });
    };

    const handleSync = () => {
        onSync(Array.from(selectedWeeks));
        onClose();
    };

    return createPortal(
        <div className="fixed inset-0 bg-black/50 backdrop-blur-[2px] z-[500] flex items-center justify-center p-4 animate-fade-in">
            <div
                dir={rtl ? 'rtl' : 'ltr'}
                className="w-full max-w-sm bg-white dark:bg-slate-900 rounded-[2.5rem] shadow-2xl overflow-hidden animate-scale-in flex flex-col max-h-[min(85dvh,calc(100dvh-2rem))] border border-white/10 dark:border-slate-800"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Slim Header */}
                <div className="flex justify-between items-center p-4 border-b border-slate-50 dark:border-slate-800 bg-white dark:bg-slate-900">
                    <h3 className="text-lg font-black text-slate-800 dark:text-slate-100 tracking-tight">
                        {t('selectWeeks')}
                    </h3>
                    <button 
                        onClick={onClose}
                        className="p-1.5 text-slate-400 dark:text-slate-500 hover:text-slate-800 dark:hover:text-slate-200 transition-colors rounded-full hover:bg-slate-50 dark:hover:bg-slate-800"
                    >
                        <XIcon className="w-6 h-6" />
                    </button>
                </div>

                {/* Compact Content */}
                <div className="p-3 overflow-y-auto scrollbar-none flex-grow">
                    <WeekChecklist 
                        weeks={weeks}
                        selectedWeeks={selectedWeeks}
                        onToggleWeek={toggleWeek}
                        mealPlans={mealPlans}
                        language={language}
                        weekStartDay={weekStartDay}
                        compact={true}
                    />
                </div>

                {/* Footer */}
                <div className={`p-4 bg-slate-50/80 dark:bg-slate-800/80 flex shrink-0 ${rtl ? 'justify-start' : 'justify-end'}`}>
                    <button 
                        onClick={handleSync}
                        disabled={selectedWeeks.size === 0}
                        className="bg-slate-900 dark:bg-slate-700 text-white px-8 py-2.5 rounded-2xl text-sm font-black uppercase tracking-widest shadow-lg shadow-slate-200 dark:shadow-slate-900/50 active:scale-95 transition-all disabled:opacity-20"
                    >
                        {t('sync')}
                    </button>
                </div>
            </div>

            <style>{`
                @keyframes scaleIn {
                    0% { opacity: 0; transform: scale(0.95); }
                    100% { opacity: 1; transform: scale(1); }
                }
                .animate-scale-in {
                    animation: scaleIn 0.2s cubic-bezier(0.34, 1.56, 0.64, 1);
                }
            `}</style>
        </div>,
        document.body,
    );
};

export default CompactWeekSelector;
